import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Menu,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

import useAuth from "../../hooks/useAuth";
import {
  getMasterDocumentRequirements,
  getCustomerDocumentRequirements,
  getDocumentConfiguration,
  createCustomerDocumentRequirement,
  saveVerificationMode,
  saveFieldSelections,
  deleteCustomerDocumentRequirement,
  generateExtractionPrompt,
  updateExtractionPrompt,
} from "../../services/preonboardingService";
import AddDocumentModal from "./AddDocumentModal";
import PromptEditorModal from "./PromptEditorModal";

const DocumentConfiguration = () => {
  const { user, organisationId, customerId } = useAuth();
  const orgId = organisationId || user?.org_id || user?.organisation_id || 307;
  const custId = customerId || user?.customer_id || user?.cust_id || 394;

  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [masterDocs, setMasterDocs] = useState([]);

  // Filters
  const [hiringTypeFilter, setHiringTypeFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  // Modals
  const [modalOpen, setModalOpen] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [selectedPromptDoc, setSelectedPromptDoc] = useState(null);

  // Snackbar Notification
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Action Menu State
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, row: null, deleting: false });

  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Load Master Documents
      const masters = await getMasterDocumentRequirements();
      setMasterDocs(masters || []);

      // 2. Load Configured Document Requirements
      const customerReqs = await getCustomerDocumentRequirements(orgId, custId);
      const reqList = customerReqs?.documents || [];

      // 3. Load Dynamic Configuration (verification mode & field selections)
      let configMap = {};
      try {
        const fullConfig = await getDocumentConfiguration(orgId, custId);
        if (fullConfig?.documents) {
          fullConfig.documents.forEach((item) => {
            configMap[item.id] = item;
            configMap[item.document_requirement_id] = item;
          });
        }
      } catch (err) {
        console.warn("Full configuration could not be loaded, using fallback:", err);
      }

      // Merge data
      const mergedList = reqList.map((req) => {
        const configItem = configMap[req.id] || configMap[req.document_requirement_id];
        return {
          id: req.id,
          mapping_id: req.id,
          document_requirement_id: req.document_requirement_id,
          document_type: req.document_type,
          display_name: req.display_name,
          hiring_type: req.hiring_type,
          verification_type: configItem?.verification_mode || (req.document_type === "EXPERIENCE_LETTER" ? "AI" : "MANUAL"),
          fields_count: configItem?.selections?.length ?? (req.document_type === "EXPERIENCE_LETTER" ? 0 : 3),
          status: req.is_active ? "Active" : "Inactive",
          raw_config: configItem,
        };
      });

      // If empty or initial setup, add standard default items if needed
      if (mergedList.length === 0) {
        setDocuments([
          {
            id: 1,
            document_requirement_id: 1,
            document_type: "PAN",
            display_name: "PAN Card",
            hiring_type: "ALL",
            verification_type: "MANUAL",
            fields_count: 3,
            status: "Active",
          },
          {
            id: 2,
            document_requirement_id: 2,
            document_type: "AADHAAR",
            display_name: "Aadhaar Card",
            hiring_type: "ALL",
            verification_type: "MANUAL",
            fields_count: 4,
            status: "Active",
          },
          {
            id: 3,
            document_requirement_id: 3,
            document_type: "DRIVING_LICENSE",
            display_name: "Driving Licence",
            hiring_type: "BLUE_COLLAR",
            verification_type: "MANUAL",
            fields_count: 3,
            status: "Active",
          },
          {
            id: 4,
            document_requirement_id: 4,
            document_type: "EXPERIENCE_LETTER",
            display_name: "Experience Letter",
            hiring_type: "WHITE_COLLAR",
            verification_type: "AI",
            fields_count: 0,
            status: "Active",
          },
          {
            id: 5,
            document_requirement_id: 5,
            document_type: "ADDRESS_PROOF",
            display_name: "Address Proof",
            hiring_type: "ALL",
            verification_type: "MANUAL",
            fields_count: 2,
            status: "Active",
          },
        ]);
      } else {
        setDocuments(mergedList);
      }
    } catch (error) {
      console.error("Failed to load document configuration data:", error);
      setSnackbar({
        open: true,
        message: "Failed to load document configuration. Please check API server connection.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, custId]);

  // Filtered documents
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      // Hiring type filter
      if (hiringTypeFilter !== "ALL") {
        const docHT = (doc.hiring_type || "").toUpperCase();
        if (docHT !== "ALL" && docHT !== hiringTypeFilter) return false;
      }

      // Status filter
      if (statusFilter !== "ALL") {
        if (doc.status?.toUpperCase() !== statusFilter.toUpperCase()) return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesName = doc.display_name?.toLowerCase().includes(query);
        const matchesType = doc.document_type?.toLowerCase().includes(query);
        if (!matchesName && !matchesType) return false;
      }

      return true;
    });
  }, [documents, hiringTypeFilter, statusFilter, searchQuery]);

  const handleSaveConfiguration = async (configData) => {
    try {
      // Step 1: Create requirement mapping (supports standard catalog & custom others)
      const createRes = await createCustomerDocumentRequirement({
        orgId,
        customerId: custId,
        hiringType: configData.hiringType,
        documentRequirementId: configData.documentRequirementId,
        isCustom: configData.isCustom,
        documentType: configData.documentType,
        displayName: configData.displayName,
        isMandatory: true,
      });

      // Step 2: Fetch updated list to resolve the exact mapping ID
      let mappingId = createRes?.id || createRes?.customer_document_requirement_id;
      if (!mappingId) {
        try {
          const reqsData = await getCustomerDocumentRequirements(orgId, custId);
          const matched = (reqsData?.documents || []).find(
            (d) =>
              d.document_type === configData.documentType &&
              (!configData.hiringType || d.hiring_type === configData.hiringType)
          );
          mappingId = matched?.id;
        } catch (e) {
          console.warn("Could not reload customer document requirements to match mapping ID:", e);
        }
      }

      // Step 3: Save verification mode
      if (mappingId) {
        try {
          await saveVerificationMode(mappingId, orgId, custId, configData.verificationMode);
        } catch (e) {
          console.warn("Could not save verification mode to server:", e);
        }

        // Step 4: Save field selections (used by both Manual and AI verification)
        if (configData.fields?.length > 0) {
          try {
            await saveFieldSelections(mappingId, orgId, custId, configData.fields);
          } catch (e) {
            console.warn("Could not save field selections to server:", e);
          }
        }

        // Step 5: For AI Verification, initialize/sync the extraction prompt
        if (configData.verificationMode === "AI") {
          try {
            if (configData.aiPrompt?.trim()) {
              try {
                await updateExtractionPrompt(mappingId, orgId, custId, configData.aiPrompt.trim());
              } catch (updErr) {
                await generateExtractionPrompt(mappingId, orgId, custId, true);
                await updateExtractionPrompt(mappingId, orgId, custId, configData.aiPrompt.trim());
              }
            } else if (configData.fields?.length > 0) {
              await generateExtractionPrompt(mappingId, orgId, custId, true);
            }
          } catch (e) {
            console.warn("Could not setup AI extraction prompt:", e);
          }
        }
      }

      setSnackbar({
        open: true,
        message: `Successfully configured ${configData.displayName}!`,
        severity: "success",
      });

      // Reload list
      await loadData();
    } catch (err) {
      console.error("Error during document configuration save:", err);
      throw err;
    }
  };

  const handleOpenMenu = (event, row) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setSelectedRow(null);
  };

  const handleOpenDelete = (row) => {
    setDeleteConfirm({ open: true, row, deleting: false });
  };

  const handleCloseDelete = () => {
    if (deleteConfirm.deleting) return;
    setDeleteConfirm({ open: false, row: null, deleting: false });
  };

  const handleConfirmDelete = async () => {
    const row = deleteConfirm.row;
    if (!row) return;

    setDeleteConfirm((prev) => ({ ...prev, deleting: true }));
    try {
      const targetId = row.mapping_id || row.id || row.document_requirement_id;
      await deleteCustomerDocumentRequirement(targetId, orgId, custId);
      setSnackbar({
        open: true,
        message: `"${row.display_name}" removed from document configuration successfully.`,
        severity: "success",
      });
      setDeleteConfirm({ open: false, row: null, deleting: false });
      await loadData();
    } catch (err) {
      console.error("Error deleting customer document requirement:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || err.message || "Failed to remove document requirement.",
        severity: "error",
      });
      setDeleteConfirm((prev) => ({ ...prev, deleting: false }));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F9FAFB", minHeight: "100vh" }}>
      {/* Page Header (Mockup 1) */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700} color="#111827">
            Document Configuration
          </Typography>
          <Typography variant="body2" color="#6B7280" mt={0.5}>
            Configure document types and extraction fields.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setModalOpen(true)}
          sx={{
            bgcolor: "#2563EB",
            "&:hover": { bgcolor: "#1D4ED8" },
            textTransform: "none",
            borderRadius: 2,
            px: 2.5,
            py: 1,
            fontWeight: 600,
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
          }}
        >
          Add Document
        </Button>
      </Box>

      {/* Filter Toolbar (Mockup 1) */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2.5,
          border: "1px solid #E5E7EB",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          bgcolor: "#FFFFFF",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="filter-hiring-type-label">Hiring Type</InputLabel>
          <Select
            labelId="filter-hiring-type-label"
            value={hiringTypeFilter}
            label="Hiring Type"
            onChange={(e) => setHiringTypeFilter(e.target.value)}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="BLUE_COLLAR">Blue Collar</MenuItem>
            <MenuItem value="WHITE_COLLAR">White Collar</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="filter-status-label">Status</InputLabel>
          <Select
            labelId="filter-status-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="ALL">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search document type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flexGrow: 1, minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Data Table (Mockup 1) */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1px solid #E5E7EB",
          overflow: "hidden",
          bgcolor: "#FFFFFF",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress size={36} color="primary" />
          </Box>
        ) : (
          <Table>
            <TableHead sx={{ bgcolor: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "#4B5563" }}>Document Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#4B5563" }}>Hiring Type</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#4B5563" }}>Fields Count</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#4B5563" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#4B5563" }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocuments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: "#6B7280" }}>
                    No document configurations found matching the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                filteredDocuments.map((row) => {
                  return (
                    <TableRow key={row.id} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                      {/* Document Type */}
                      <TableCell sx={{ fontWeight: 600, color: "#111827" }}>
                        {row.display_name}
                      </TableCell>

                      {/* Hiring Type */}
                      <TableCell sx={{ color: "#4B5563" }}>
                        {row.hiring_type === "BLUE_COLLAR"
                          ? "Blue Collar"
                          : row.hiring_type === "WHITE_COLLAR"
                          ? "White Collar"
                          : "All"}
                      </TableCell>

                      {/* Fields Count */}
                      <TableCell sx={{ color: "#374151" }}>{row.fields_count}</TableCell>

                      {/* Status */}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <FiberManualRecordIcon
                            sx={{
                              fontSize: 10,
                              color: row.status === "Active" ? "#10B981" : "#9CA3AF",
                            }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 500, color: "#374151" }}>
                            {row.status}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                          <Tooltip title="Edit Configuration">
                            <IconButton
                              size="small"
                              onClick={() => setModalOpen(true)}
                              sx={{ color: "#6B7280", "&:hover": { color: "#2563EB" } }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete Document">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDelete(row)}
                              sx={{ color: "#EF4444", "&:hover": { color: "#B91C1C", bgcolor: "#FEE2E2" } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            sx={{ color: "#6B7280", "&:hover": { color: "#111827" } }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Row More Actions Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleCloseMenu}>
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            setModalOpen(true);
          }}
        >
          Add / Reconfigure Document
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = selectedRow;
            handleCloseMenu();
            if (row) {
              setDocuments((prev) =>
                prev.map((d) =>
                  d.id === row.id
                    ? { ...d, status: d.status === "Active" ? "Inactive" : "Active" }
                    : d
                )
              );
              setSnackbar({
                open: true,
                message: `Status updated for ${row.display_name}.`,
                severity: "success",
              });
            }
          }}
        >
          Toggle Active Status
        </MenuItem>
        <MenuItem
          onClick={() => {
            const row = selectedRow;
            handleCloseMenu();
            if (row) handleOpenDelete(row);
          }}
          sx={{ color: "#DC2626" }}
        >
          Remove / Delete Document
        </MenuItem>
      </Menu>

      {/* 3-Step Add Document Modal (Mockups 2-6) */}
      <AddDocumentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        masterDocs={masterDocs}
        configuredDocs={documents}
        onSaveConfiguration={handleSaveConfiguration}
      />

      {/* AI Extraction Prompt Modal */}
      <PromptEditorModal
        open={promptModalOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          setPromptModalOpen(false);
          setSelectedPromptDoc(null);
        }}
        documentConfig={selectedPromptDoc}
        orgId={orgId}
        customerId={custId}
        onSaved={loadData}
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirm.open}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#111827" }}>
          Remove Document Requirement?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#4B5563", fontSize: "0.9rem" }}>
            Are you sure you want to remove <strong>{deleteConfirm.row?.display_name}</strong> ({deleteConfirm.row?.hiring_type === "BLUE_COLLAR" ? "Blue Collar" : deleteConfirm.row?.hiring_type === "WHITE_COLLAR" ? "White Collar" : "All"})?
            <br /><br />
            This document will no longer be requested for newly onboarded candidates. Existing candidates who have already submitted this document will retain their history.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseDelete}
            disabled={deleteConfirm.deleting}
            sx={{ textTransform: "none", color: "#6B7280" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleteConfirm.deleting}
            sx={{
              bgcolor: "#DC2626",
              "&:hover": { bgcolor: "#B91C1C" },
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {deleteConfirm.deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentConfiguration;
